/**
 * `cad` namespace catalog (ADR 0310 Phase C) — the CAD canvas type's vocabulary under the canvas framework's fixed type-key contract; chassis strings live in the `canvas` namespace.
 */
export const messages = {
  editorHeading: 'CAD model editor',
  docName: 'Model name',
  deleteDoc: 'Delete model',
  deleteDocTitle: 'Delete "{{name}}"?',
  deleteDocBody: 'The model working copy and its version history are permanently removed. The chat artifact it was opened from is not affected. This cannot be undone.',
  deleteDocDone: 'Model deleted.',
  deleteDocFailed: 'Could not delete this model.',
  col_solids: 'Solids',
  add_box: 'Box',
  add_cylinder: 'Cylinder',
  add_sphere: 'Sphere',
  add_cone: 'Cone',
  kind_box: 'Box',
  kind_cylinder: 'Cylinder',
  kind_sphere: 'Sphere',
  kind_cone: 'Cone',
  kind_solid: 'Solid',
  interactiveLabel: 'Model canvas — click a solid to select; drag to move, drag the knob to rotate, drag an edge handle to resize',
  // ADR 0340 — catalog prop/option labels (the chassis PropertyField
  // resolves prop_<name> / opt_<name>_<value> with the code label as fallback).
  prop_units: "Units",
  prop_x: 'X', prop_y: 'Y', prop_z: 'Z',
  prop_rotation: 'Rotation (°)',
  prop_materialId: 'Material',
  materialNone: "— inline colour —",
  // CAD-G1 — the catalog ids are kebab wire tokens; these are their labels.
  "opt_materialId_steel": "Steel",
  "opt_materialId_aluminum": "Aluminium",
  "opt_materialId_brass": "Brass",
  "opt_materialId_copper": "Copper",
  "opt_materialId_gold": "Gold",
  "opt_materialId_plastic-red": "Plastic — red",
  "opt_materialId_plastic-blue": "Plastic — blue",
  "opt_materialId_plastic-black": "Plastic — black",
  "opt_materialId_plastic-white": "Plastic — white",
  "opt_materialId_rubber": "Rubber",
  "opt_materialId_glass": "Glass",
  "opt_materialId_wood-oak": "Wood — oak",
  opt_tolType_symmetric: "Symmetric (±)",
  opt_tolType_asymmetric: "Asymmetric (+/−)",
  opt_tolType_limit: "Limit (upper/lower)",
  prop_color: 'Color',
  prop_metallic: 'Metallic (0–1)',
  prop_roughness: 'Roughness (0–1)',
  prop_emissive: 'Emissive color',
  prop_label: 'Label',
  prop_width: 'Width', prop_height: 'Height', prop_depth: 'Depth',
  prop_radius: 'Radius', prop_length: 'Length',
  prop_scale: 'Scale',
  prop_solid: 'Solid #',
  prop_axis: 'Axis',
  prop_tolType: 'Tolerance type',
  prop_tolA: 'Tolerance A (+)',
  prop_tolB: 'Tolerance B (−)',
  prop_tolUpper: "Upper limit",
  prop_tolLower: "Lower limit",

  // ADR 0388 P1 — mesh interchange
  kind_mesh: 'Mesh',
  meshSimplified: 'Simplified preview — {{shown}} of {{total}} triangles shown; exports keep full detail.',
  meshLoadingBadge_one: '{{formattedCount}} mesh still loading — a placeholder box is shown.',
  meshLoadingBadge_other: '{{formattedCount}} meshes still loading — placeholder boxes are shown.',
  meshFailedBadge_one: '{{formattedCount}} mesh failed to load — the box shown is a placeholder, not the part.',
  meshFailedBadge_other: '{{formattedCount}} meshes failed to load — the boxes shown are placeholders, not the parts.',
  viewCubeLabel: 'Orientation',
  viewCube_front: 'Front',
  viewCube_back: 'Back',
  viewCube_left: 'Left',
  viewCube_right: 'Right',
  viewCube_top: 'Top',
  viewCube_bottom: 'Bottom',
  viewCubeFaceAria: 'Snap to the {{view}} view',
  viewCubeYawLeft: 'Rotate left 90\u00b0',
  viewCubeYawRight: 'Rotate right 90\u00b0',
  viewCubePitchUp: 'Tilt up 90\u00b0',
  viewCubePitchDown: 'Tilt down 90\u00b0',
  viewCubeHome: 'Reset to the home view',
  assistOpen: 'Ask the CAD Modeler',
  assistHeading: 'CAD Modeler',
  assistClose: 'Close',
  assistHint: 'Changes land in this model live and are undoable with the editor history.',
  assistLoading: 'Loading the assistant\u2026',
  assistWelcome: 'Describe what to model or change \u2014 the CAD Modeler reads this model first, then renders validated parametric solids.',
  assistExample1: 'Read model {{canvasId}} and describe what it contains.',
  assistExample2: 'In model {{canvasId}}, add a 20mm mounting boss on the top face.',
  assistExample3: 'Rework model {{canvasId}} into a two-part enclosure with 2mm walls.',
  importMesh: 'Import STL/OBJ/glTF',
  exportStl: 'Export STL',
  exportGlb: 'Export GLB',
  exportPng: 'Export PNG',
  importDone: 'Mesh imported.',
  importDropped: 'Imported geometry only — dropped: {{dropped}}.',
  importFailed: 'Could not import that file.',
  importUnsupported: 'Unsupported file type — use .stl, .obj, .gltf, or .glb.',
  exportFailed: 'Export failed.',
  meshStillLoading: 'A referenced mesh is still loading — try again in a moment.',

  // ADR 0388 P2 — BOM
  bomBtn: 'BOM (CSV)',
  bomFailed: 'Could not generate the BOM.',
  bomInvalid: 'This bill of materials could not be read.',
  bomCaption: 'Bill of materials{{name}}',
  bomColLabel: 'Part',
  bomColKind: 'Kind',
  bomColQty: 'Qty',
  bomColDims: 'Dimensions',
  bomColMaterial: 'Material',
  bomColVolume: 'Volume ({{units}}³)',
  bomColArea: 'Area ({{units}}²)',
  bomApprox: 'approx.',
  bomTotal: 'Total',
  bomMeta: '{{parts}} parts · {{units}}',

  // ADR 0388 P3 — dimensions
  col_dimensions: 'Dimensions',
  'add_dim-linear': 'Linear',
  'add_dim-diameter': 'Diameter',
  'add_dim-radial': 'Radial',
  'add_dim-angular': 'Angular',
  dim_linear: 'Linear',
  dim_angular: 'Angular',
  dim_radial: 'Radial',
  dim_diameter: 'Diameter',
  dim_arc: 'Arc',
  dim_ordinate: 'Ordinate',

  // ADR 0388 §Correction — the editor's 2D/3D view toggle + a named busy state
  // per interchange operation.
  viewLabel: 'Model view',
  view2d: '2D',
  view3d: '3D',
  interactive3dLabel: 'Model orbit view — drag or press the arrow keys to rotate. Read-only.',
  view3dLoading: 'Loading the 3D view…',
  view3dError: 'The 3D view could not be rendered. The 2D editor is still available.',
  view3dReadOnly: 'Read-only view — drag or press the arrow keys to orbit. To edit, switch to 2D or use the solid list and property panel.',
  busy_import: 'Importing mesh…',
  busy_stl: 'Exporting STL…',
  busy_glb: 'Exporting GLB…',
  busy_png: 'Exporting PNG…',
  busy_bom: 'Generating the bill of materials…',
  // ADR 0768 follow-up P1 — the embed's honest "can see" scope (the agent is NOT bound to what is on screen)
  assistCanSee: "CAD models you can open in this workspace",
  // ADR 0777 — the embed's label once bound to the open model
  assistCanSeeThis: "this model",
};
